import { Monogram } from '@/components/ui/Monogram'
import { Badge } from '@/components/ui/Badge'

import { Text } from '@/components/ui/Text'
import { Button } from '@/components/ui/Button'

interface Day0IdentityHeaderProps {
  orgName: string
  /** null → Owner label and value are absent (not '—') */
  ownerName: string | null
  /** null → Created label and value are absent (not '—'). ISO date string when available. */
  createdAt: string | null
  /** null → CTA is hidden */
  ctaLabel: string | null
  onCta: () => void
  /** Wired to /settings/invite-member from the parent */
  onAssignTeam?: () => void
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

export function Day0IdentityHeader({
  orgName,
  ownerName,
  createdAt,
  ctaLabel,
  onCta,
  onAssignTeam,
}: Day0IdentityHeaderProps) {
  const hasMeta = ownerName !== null || createdAt !== null

  return (
    <div
      data-testid="day0-identity-header"
      className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
    >
      {/* Left: monogram + identity */}
      <div className="flex items-start gap-4">
        <Monogram name={orgName} size="lg" shape="rounded" />
        <div className="flex flex-col gap-1">
          <Text variant="headline-md">{orgName}</Text>
          <Badge variant="success" className="self-start">Onboarding</Badge>
        </div>
      </div>

      {/* Right: meta + actions */}
      <div className="flex flex-col gap-3 sm:items-end">
        {/* Owner + created-date — omit fields with no data; whole row omits when both null */}
        {hasMeta && (
          <div className="flex gap-4">
            {ownerName !== null && (
              <div className="flex flex-col">
                <Text variant="label-sm" color="subtle">Owner</Text>
                <Text variant="body-sm" color="muted">{ownerName}</Text>
              </div>
            )}
            {createdAt !== null && (
              <div className="flex flex-col">
                <Text variant="label-sm" color="subtle">Created</Text>
                <Text variant="body-sm" color="muted">{formatDate(createdAt)}</Text>
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onAssignTeam}>
            Assign team
          </Button>

          {ctaLabel && (
            <Button variant="default" size="sm" onClick={onCta}>
              {ctaLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
